import { PdBaseUI } from "./pd-base-ui.js";
import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-hover-box.js";
import { SharedInputStyles } from "../styles/shared-input-styles.js";
import "../types.js";
import { emailValidator, numberValidator, phoneValidator, requiredCheckedValidator, requiredRangeValidator, requiredSelectValidator, requiredValidator, vatValidator } from "../validators.js";
import { css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*
* Abstract base lit class for pd-input elements.
*
* Used from:
* - pd-checkbox
* - pd-input
* - pd-input-area
* - pd-radio-group
* - pd-range
* - pd-select
*
* Used to:
* - define css (import additional shared-input-styles.js)
* - define common properties (detailieren, aktuell zu viele bzw. nicht für alle sub-elemente gültig)
* - handle error msg (getter/setter)
* - helpers for validation event after change and render label/error elements.
*
* Events:
*  - validate-form => fired when input element changed its value.
*  - enter-pressed => fired when enter pressed.
*  - pd-form-element-change => fired when input element changed its value.
*
* Custom Properties (shared-input-styles):
* ... TODO
*
*/
/**
* Specific key codes for input events.
*/
var KEY_RETURN = "Enter";
/**
* Abstract base class for pd-input elements.
*
*
* @event validate-form - Fired when input value changes for form validation.
* @event enter-pressed - Fired when Enter key pressed during focus.
* @event pd-form-element-change - Fired when field value changes with state details.
*/
var PdBaseUIInput = class extends PdBaseUI {
	constructor(..._args) {
		super(..._args);
		this.gradient = false;
		this.disabled = false;
		this.readonly = false;
		this.required = false;
		this.requiredMsg = "";
		this.helperTxt = "";
		this.valueName = "";
		this.defaultRequiredChar = "*";
		this.label = "";
		this.fieldType = "text";
		this.initValue = "";
		this.internalElement = false;
		this.noLabel = false;
		this.reserveLabel = false;
		this.handleChangeForInitVal = false;
		this.span = "full";
		this._valid = false;
		this._invalid = false;
		this._dirty = false;
		this._pristine = true;
		this._touched = false;
		this._focused = false;
		this._errorMsg = "";
		this._value = "";
		this._inputType = -1;
		this._phase = "init";
		this._validators = [];
	}
	static {
		this.styles = [
			PdBaseUI.styles,
			SharedInputStyles,
			css`
      .label-header {
        display: flex;
        align-items: end;
        justify-content: space-between;
        width: var(--pd-input-field-width, 250px);
        max-width: 100%;
        min-width: 150px;
        box-sizing: border-box;
      }

      :host([reserve-label]) .label-header {
        min-height: var(--pd-input-label-font-size, 0.9em);
      }

      .help-icon {
        --pd-icon-size: 20px;
        cursor: help;
      }

      .label-icons {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .ai-trigger {
        --pd-icon-size: 22px;
        cursor: pointer;
        opacity: 0.7;
        transition: opacity 0.2s;
      }
      .ai-trigger:hover,
      .ai-trigger:focus-visible {
        opacity: 1;
      }
    `
		];
	}
	update(changedProps) {
		const hasInitValueChange = changedProps.has("initValue");
		const condition = this.initValue || changedProps.get("initValue");
		if (hasInitValueChange && condition) {
			if (this._phase === "ready" && this.initValue && this.initValue !== "UNDEF") this._handleChangedValue(this.initValue, void 0, true, false, void 0, true);
			else this._value = this.initValue;
		}
		super.update(changedProps);
	}
	firstUpdated() {
		this.runValidators(true).then((result) => {
			this._registerAtContainer(result);
			this._phase = "ready";
		});
	}
	disconnectedCallback() {
		this._unRegisterAtContainer();
		super.disconnectedCallback();
	}
	/** Current validation error message. */
	get errorMsg() {
		return this._errorMsg;
	}
	set errorMsg(msg) {
		this._errorMsg = msg;
	}
	/** Current input value. */
	get value() {
		return this._value;
	}
	set value(val) {
		this._handleChangedValue(val, void 0, true);
	}
	/** Set error state manually. */
	set error(val) {
		this._invalid = val;
	}
	/** Set valid state manually. */
	set valid(isValid) {
		this._valid = isValid;
	}
	reset() {
		this._value = this._getInitialValue(true);
		this.runValidators(true).then((result) => {
			this._errorMsg = "";
			this._valid = false;
			this._invalid = false;
			this._pristine = true;
			this._dirty = false;
			this._touched = false;
			this._focused = false;
			this.dispatchEvent(new CustomEvent("pd-form-element-change", {
				detail: {
					value: this._value,
					parsedValue: this._getParsedValue(),
					name: this.valueName || this.id || "field",
					state: {
						pristine: this._pristine,
						dirty: this._dirty,
						touched: this._touched,
						focused: this._focused,
						valid: result,
						invalid: !result
					}
				},
				bubbles: true,
				composed: true
			}));
		});
	}
	clear() {
		this.reset();
		this._value = "";
	}
	_registerAtContainer(valid) {
		this.dispatchEvent(new CustomEvent("pd-form-element-register", {
			detail: {
				name: this.valueName || this.id || "field",
				value: this._getInitialValue(),
				parsedValue: this._getParsedValue(),
				state: {
					pristine: this._pristine,
					dirty: this._dirty,
					touched: this._touched,
					focused: this._focused,
					valid,
					invalid: !valid
				}
			},
			bubbles: true,
			composed: true
		}));
	}
	_unRegisterAtContainer() {
		document.dispatchEvent(new CustomEvent("pd-form-element-unregister", {
			detail: {
				name: this.valueName || this.id || "field",
				value: this._value
			},
			bubbles: true,
			composed: true
		}));
	}
	_collectValidators() {
		const validators = [];
		if (this.required) {
			validators.push(requiredValidator);
			if (this._inputType === 2) validators.push(requiredSelectValidator);
			if (this._inputType === 4) validators.push(requiredRangeValidator);
			if (this._inputType === 3) validators.push(requiredCheckedValidator);
		}
		if (this.fieldType === "mail") validators.push(emailValidator);
		if (this.fieldType === "number") validators.push(numberValidator);
		if (this.fieldType === "phone") validators.push(phoneValidator);
		if (this.fieldType === "vat") validators.push(vatValidator);
		return [...validators, ...this._validators];
	}
	/**
	* @param controlled - true only for the ready-phase `initValue` update:
	*   the value changes, but the field does NOT become dirty/non-pristine —
	*   dirty tracks user interaction, anchored at the (re)initialized value.
	*/
	_handleChangedValue(newValue, event, dispatchEvent = false, checkReturn = false, callback, controlled = false) {
		const changed = this._value !== newValue;
		let keyCode = "-1";
		if (event && event instanceof KeyboardEvent) keyCode = event.key;
		const isReturn = checkReturn && keyCode === "Enter";
		const dispatchEnter = () => this.dispatchEvent(new CustomEvent("enter-pressed", {
			detail: {
				value: newValue,
				name: this.valueName || this.id || "field",
				keyCode,
				changed
			},
			bubbles: true,
			composed: true
		}));
		if (isReturn) event?.preventDefault();
		if (changed) {
			this._value = newValue;
			if (!controlled) {
				this._pristine = false;
				this._dirty = true;
			}
			this.runValidators().then((result) => {
				if (dispatchEvent) this._dispatchChange(newValue);
				if (isReturn) dispatchEnter();
				callback?.();
			});
		} else if (isReturn) dispatchEnter();
	}
	_dispatchChange(value, eventName) {
		this.dispatchEvent(new CustomEvent(eventName || "pd-form-element-change", {
			detail: {
				value,
				name: this.valueName || this.id || "field",
				parsedValue: this._getParsedValue(),
				state: {
					pristine: this._pristine,
					dirty: this._dirty,
					touched: this._touched,
					focused: this._focused,
					valid: this._valid,
					invalid: this._invalid
				}
			},
			bubbles: true,
			composed: true
		}));
	}
	getClassmap(specific = {}) {
		return {
			...specific,
			error: this._invalid,
			valid: this._valid && this._value && this._value !== "UNDEF",
			focus: this._focused,
			prestine: this._pristine,
			dirty: this._dirty
		};
	}
	_renderErrorMsg(errorId) {
		return this._errorMsg ? html`
          <div id="${errorId || ""}" class="error-box error">
            <p class="error-msg">${this._errorMsg}</p>
          </div>
        ` : "";
	}
	_renderLabel(forParam, additionalValue) {
		if (this.noLabel) return nothing;
		if (!this.label && !this.helperTxt && !additionalValue && !this.aiOptions && !this.reserveLabel) return nothing;
		return html`
      <div class="label-header">
        <label for="${forParam}">
          ${this.label}${this.required && this.label ? this.defaultRequiredChar : ""}
          ${additionalValue ? html` - <b>${additionalValue}</b>` : ""}
        </label>
        <div class="label-icons">
          ${this.helperTxt ? html`
                  <pd-hover-box>
                    <pd-icon
                      slot="normal-view"
                      icon="${pdIcons.ICON_HELP}"
                      activeIcon
                      class="help-icon"
                    ></pd-icon>
                    <p slot="info-view">${this.helperTxt}</p>
                  </pd-hover-box>
                ` : ""}
          ${this._renderExtraLabelIcons()}
          ${this.aiOptions ? html`
                  <pd-icon
                    icon="${pdIcons.ICON_AI_SPARKLE}"
                    activeIcon
                    class="ai-trigger"
                    tabindex="0"
                    role="button"
                    aria-label="KI-Assistent öffnen"
                    @click="${this._onAiTrigger}"
                    @keydown="${this._onAiTriggerKeyDown}"
                  ></pd-icon>
                ` : ""}
        </div>
      </div>
    `;
	}
	_renderExtraLabelIcons() {
		return nothing;
	}
	_onAiTrigger() {
		this.dispatchEvent(new CustomEvent("pd-ai-trigger", {
			bubbles: true,
			composed: true,
			detail: {
				value: this._value,
				valueName: this.valueName,
				aiOptions: this.aiOptions
			}
		}));
	}
	_onAiTriggerKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this._onAiTrigger();
		}
	}
	runValidators(dryRun) {
		if (!dryRun) {
			this._errorMsg = "";
			this._valid = true;
			this._invalid = false;
		}
		const validators = this._collectValidators();
		const asyncChecks = [];
		for (const validator of validators) {
			const result = validator(this._value);
			if (result instanceof Promise) asyncChecks.push(result);
			else if (result) {
				if (!dryRun) {
					this._errorMsg = result;
					this._valid = false;
					this._invalid = true;
					if (this._inputType === 7) this._handleStatusForInnerInputElement(false);
				}
				return Promise.resolve(false);
			}
		}
		if (asyncChecks.length > 0) return Promise.all(asyncChecks).then((results) => {
			const firstError = results.find((r) => r !== null);
			if (firstError) {
				if (!dryRun) {
					this._errorMsg = firstError;
					this._valid = false;
					this._invalid = true;
					if (this._inputType === 7) this._handleStatusForInnerInputElement(false);
				}
				return Promise.resolve(false);
			} else if (!dryRun) {
				this._errorMsg = "";
				this._valid = true;
				this._invalid = false;
				if (this._inputType === 7) this._handleStatusForInnerInputElement(true);
			}
			return Promise.resolve(true);
		});
		if (this._inputType === 7 && !dryRun) this._handleStatusForInnerInputElement(true);
		return Promise.resolve(true);
	}
	_handleStatusForInnerInputElement(status) {
		throw new Error("Method must be implemented in sub class to deal with this._input");
	}
	_getInitialValue(reset) {
		return reset ? this.initValue || "" : this.initValue || this._value || "";
	}
	_getParsedValue() {
		return this._value;
	}
};
__decorate([property({ type: Boolean })], PdBaseUIInput.prototype, "gradient", void 0);
__decorate([property({ type: Boolean })], PdBaseUIInput.prototype, "disabled", void 0);
__decorate([property({ type: Boolean })], PdBaseUIInput.prototype, "readonly", void 0);
__decorate([property({ type: Boolean })], PdBaseUIInput.prototype, "required", void 0);
__decorate([property({ type: String })], PdBaseUIInput.prototype, "requiredMsg", void 0);
__decorate([property({ type: String })], PdBaseUIInput.prototype, "helperTxt", void 0);
__decorate([property({ type: String })], PdBaseUIInput.prototype, "valueName", void 0);
__decorate([property({ type: String })], PdBaseUIInput.prototype, "defaultRequiredChar", void 0);
__decorate([property({ type: String })], PdBaseUIInput.prototype, "label", void 0);
__decorate([property({ type: String })], PdBaseUIInput.prototype, "fieldType", void 0);
__decorate([property({ type: String })], PdBaseUIInput.prototype, "initValue", void 0);
__decorate([property({ type: Boolean })], PdBaseUIInput.prototype, "internalElement", void 0);
__decorate([property({
	type: Boolean,
	attribute: "no-label"
})], PdBaseUIInput.prototype, "noLabel", void 0);
__decorate([property({
	type: Boolean,
	attribute: "reserve-label"
})], PdBaseUIInput.prototype, "reserveLabel", void 0);
__decorate([property({ type: Boolean })], PdBaseUIInput.prototype, "handleChangeForInitVal", void 0);
__decorate([property({
	type: String,
	reflect: true
})], PdBaseUIInput.prototype, "span", void 0);
__decorate([property({
	type: Object,
	attribute: false
})], PdBaseUIInput.prototype, "aiOptions", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_valid", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_invalid", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_dirty", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_pristine", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_touched", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_focused", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_errorMsg", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_value", void 0);
__decorate([state()], PdBaseUIInput.prototype, "_inputType", void 0);
//#endregion
export { KEY_RETURN, PdBaseUIInput };
